/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */
import "@fontsource/merriweather/300.css"
import "@fontsource/merriweather/400.css"
import "@fontsource/merriweather/700.css"
import "@fontsource/merriweather/900.css"
import "@fontsource/open-sans/300.css"
import "@fontsource/open-sans/400.css"
import "@fontsource/open-sans/500.css"
import "@fontsource/open-sans/600.css"
import "@fontsource/open-sans/700.css"
import "@fontsource/open-sans/800.css"
import "./src/styles/global.css"

type NetlifyIdentityWidget = {
  on: (event: "init" | "login", callback: (user?: unknown) => void) => void
  init: () => void
}

declare global {
  interface Window {
    netlifyIdentity?: NetlifyIdentityWidget
  }
}

const identityTokenRoute = /(confirmation|invite|recovery|email_change)_token=([^&]+)/
const identityErrorRoute = /error=access_denied&error_description=403/
const identityAccessTokenRoute = /access_token=/
const identityWidgetScriptPath = "/admin/netlify-identity.js"

const loadNetlifyIdentityWidget = (): Promise<NetlifyIdentityWidget> => {
  if (window.netlifyIdentity) {
    return Promise.resolve(window.netlifyIdentity)
  }

  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector(
      "script[data-netlify-identity-widget='true']"
    ) as HTMLScriptElement | null

    const resolveFromWindow = () => {
      if (window.netlifyIdentity) {
        resolve(window.netlifyIdentity)
        return
      }

      reject(new Error("netlifyIdentity global is missing after script load"))
    }

    if (existingScript) {
      existingScript.addEventListener("load", resolveFromWindow, { once: true })
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Failed to load netlify-identity script")),
        { once: true }
      )
      return
    }

    const script = document.createElement("script")
    script.src = identityWidgetScriptPath
    script.async = true
    script.defer = true
    script.dataset.netlifyIdentityWidget = "true"
    script.addEventListener("load", resolveFromWindow, { once: true })
    script.addEventListener(
      "error",
      () => reject(new Error(`Failed to load ${identityWidgetScriptPath}`)),
      { once: true }
    )
    document.head.appendChild(script)
  })
}

export const onInitialClientRender = () => {
  const hash = (window.location.hash || "").replace(/^#\/?/, "")

  if (
    !identityTokenRoute.test(hash) &&
    !identityErrorRoute.test(hash) &&
    !identityAccessTokenRoute.test(hash)
  ) {
    return
  }

  loadNetlifyIdentityWidget()
    .then(netlifyIdentity => {
      netlifyIdentity.on("init", user => {
        if (!user) {
          netlifyIdentity.on("login", () => {
            window.location.href = "/admin/"
          })
        }
      })

      netlifyIdentity.init()
    })
    .catch(error => {
      console.error("Failed to load netlify-identity-widget", error)
    })
}
